import React, {useEffect} from "react";
import {
	Button,
	Row,
	Col,
	Container,
	Image,
} from "react-bootstrap";
import {Helmet} from "react-helmet";
import {Link} from "react-router-dom";
import {useAuth} from "../../AuthUser";
import API from "../../api";
import swingAnalysisCollage from "../../assets/img/swing-analysis-collage.jpg";

const steps = [
	{
		icon: "bi-1-circle-fill",
		title: "Build your DNA Profile",
		text: "A quick wizard about your game, your goals, and your body. You must complete it before accessing the site — it's how we personalize everything for you.",
	},
	{
		icon: "bi-2-circle-fill",
		title: "Upload your first swing",
		text: "Film your swing, submit it, and your instructor will get to work on your first virtual lesson.",
	},
	{
		icon: "bi-3-circle-fill",
		title: "Get your game plan",
		text: "You'll receive a detailed video review with personalized drills — and we keep refining your plan every step of the way.",
	},
];

const Thankyou = () => {
	const { setUser, user } = useAuth();
	useEffect(() => {
		API.get(`/accounts/me`)
			.then(response => setUser(response.data))
	}, [])
	return (
		<>
			<Helmet>
				<title>Thankyou! | MyGolfDNA.com</title>
				<meta name="description" content="build your dna profile"/>
			</Helmet>
			<style>{`
				@media (max-width: 767px) {
					/* Same mobile edge treatment as the homepage: 24px container
					   padding absorbs the -24px row margins of the g-5 row. */
					#thankyou-page .container {
						padding-left: 24px;
						padding-right: 24px;
					}
				}
				#thankyou-page .ty-overline {
					color: var(--orange, #ff5b25);
					font-weight: 700;
					letter-spacing: 3px;
					font-size: clamp(1rem, 1.8vw, 1.25rem);
					text-transform: uppercase;
				}
				#thankyou-page .ty-overline .bi {
					margin-right: 6px;
				}
				#thankyou-page h1 {
					font-size: clamp(1.7rem, 4vw, 2.9rem);
					line-height: 1.15;
					letter-spacing: 1px;
				}
				#thankyou-page h2 {
					font-size: clamp(1.3rem, 2.6vw, 1.9rem);
					letter-spacing: 1px;
				}
				#thankyou-page .ty-player {
					border-radius: 16px;
					overflow: hidden;
					box-shadow: 0 24px 48px -24px rgba(0, 0, 0, 0.8);
				}
				#thankyou-page .ty-photo {
					border-radius: 16px;
					box-shadow: 0 24px 48px -24px rgba(16, 23, 31, 0.5);
				}
				#thankyou-page .ty-cta .btn {
					padding: 0.85rem 3rem;
					font-size: 1.1rem;
					font-weight: 700;
					letter-spacing: 0.5px;
					text-transform: uppercase;
				}
			`}</style>
			<div id="thankyou-page">
				{/* Dark hero: payment confirmation + welcome video from Chris */}
				<div style={{backgroundColor: "#1B2631", padding: "64px 0 72px"}}>
					<Container>
						<Row className="justify-content-center text-center">
							<Col lg={9}>
								<div className="ty-overline mb-3">
									<i className="bi bi-check-circle-fill" /> Payment confirmed
								</div>
								<h1 className="monument-font-black text-white text-uppercase">
									Welcome to the team{user?.firstName ? `, ${user.firstName}` : ""}!
								</h1>
								<p className="text-white-50 mt-3 mb-4" style={{fontWeight: 300, fontSize: 20}}>
									{user?.planType
										? <>Your <span className="text-secondary fw-semibold">{user.planType}</span> membership is now active.</>
										: <>Your membership is now active.</>}
									{" "}Here&apos;s a personal welcome from Chris.
								</p>
							</Col>
						</Row>
						<Row className="justify-content-center">
							<Col lg={8} md={12} sm={12}>
								<div className="ty-player" style={{padding: "56.25% 0 0 0", position: "relative"}}>
									<iframe
										src="https://player.vimeo.com/video/826532488?h=51c8710145&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
										frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
										style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%"}}
										title="Welcome New Member - MyGolfDNA"/>
								</div>
							</Col>
						</Row>
					</Container>
				</div>

				{/* What happens next — white section keeps the page light before
				    the dark footer, matching the alternation used site-wide. */}
				<div style={{backgroundColor: "#fff", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									What happens <span className="text-secondary">next</span>
								</h2>
							</Col>
						</Row>
						<Row className="align-items-center g-5">
							<Col lg={6}>
								{steps.map((step) => (
									<div className="d-flex flex-row mb-4" key={step.title}>
										<div className="d-inline-block px-2">
											<i className={`bi ${step.icon} text-secondary h1`}/>
										</div>
										<div className="d-inline-block align-top px-2">
											<h3 className="monument-font text-uppercase" style={{fontSize: "1.15rem"}}>{step.title}</h3>
											<p className="mb-0">{step.text}</p>
										</div>
									</div>
								))}
							</Col>
							<Col lg={6}>
								{/* Real swing-analysis screens from the review tool — a
								    literal preview of what the member's lessons look like. */}
								<Image src={swingAnalysisCollage} alt="Examples of swing analysis reviews" className="ty-photo" fluid />
							</Col>
						</Row>
						<Row className="justify-content-center text-center mt-5">
							<Col lg={8}>
								<div className="ty-cta">
									<Button as={Link} to="/wizard/about" variant="secondary" size="lg" className="text-white">
										Build my DNA Profile
									</Button>
								</div>
								<div className="text-black-50 small mt-3">
									Takes about 5 minutes · Required before accessing the site
								</div>
							</Col>
						</Row>
					</Container>
				</div>
			</div>
		</>
	);
};

export default Thankyou;
